import { useRef, useState, useEffect, useCallback } from 'react';
import ResizeObserver from 'resize-observer-polyfill';
import _delay from 'lodash/delay';
import { useMappedState } from 'redux-react-hook';

export function usePrevious(value) {
  const ref = useRef();
  useEffect(() => {
    ref.current = value;
  });
  return ref.current;
}

// Hook to allow dynamic height to be set on springs https://github.com/react-spring/react-spring/issues/302#issuecomment-473718925
export function useMeasure() {
  const ref = useRef();
  const [bounds, set] = useState({
    left: 0,
    top: 0,
    width: 0,
    height: 0,
  });
  const [resizeObserver] = useState(() => new ResizeObserver(([entry]) => set(entry.contentRect)));
  useEffect(() => {
    resizeObserver.observe(ref.current);
    return () => resizeObserver.disconnect();
  }, [resizeObserver]);
  return [{ ref }, bounds];
}

export function useAutoFocus(isFirst, isMounted, elementRef) {
  const mapState = useCallback(state => ({
    isAutoFocusEnabled: state.flagsReducer.isAutoFocusEnabled,
  }), []);
  const { isAutoFocusEnabled } = useMappedState(mapState);
  return useEffect(() => {
    if (isAutoFocusEnabled && isFirst && isMounted && elementRef) {
      _delay(() => {
        if (elementRef.current) {
          return elementRef.current.focus();
        }
        return null;
      }, 100);
    }
  }, [isFirst, isMounted, elementRef, isAutoFocusEnabled]);
}

const MOTION_QUERY = '(prefers-reduced-motion: reduce)';
const getInitialMotionPreference = () => (
  window.matchMedia(MOTION_QUERY).matches
);
export function usePrefersReducedMotion() {
  const [
    prefersReducedMotion,
    setPrefersReducedMotion,
  ] = useState(getInitialMotionPreference);
  useEffect(() => {
    const mediaQueryList = window.matchMedia(MOTION_QUERY);
    if (mediaQueryList.addListener) {
      const listener = event => {
        setPrefersReducedMotion(!event.matches);
      };
      mediaQueryList.addListener(listener);
      return () => {
        mediaQueryList.removeListener(listener);
      };
    }
    return () => null;
  }, []);
  return prefersReducedMotion;
}
